import React from 'react';
import RoadmapCoursePage from './RoadmapCoursePage';
import { ROADMAP_STUDENT_FAQS } from '../utils/roadmapStudentFaqs';

const AWS_DEVOPS_ROADMAP_CONFIG = {
  path: '/roadmap-to-aws-devops-course',
  seoTitle: 'Roadmap to AWS DevOps Course | Algodoor',
  seoDescription: 'Visual roadmap for AWS Cloud and DevOps essentials with Linux, networking, deployment pipelines, and interview preparation.',
  badge: 'AWS DevOps Roadmap',
  headingPrefix: 'Roadmap to Become an',
  headingHighlight: 'AWS DevOps Engineer (Beginner Track)',
  heroText: 'This roadmap follows the same 2-month syllabus shared on our Courses page for AWS Cloud with DevOps Essentials. You learn cloud basics, deploy real apps, and understand practical DevOps workflows used in teams.',
  roadmapNotice: 'This is one of our actively offered admission tracks and a strong cloud pathway for Java-background students.',
  enrollPath: '/courses',
  syllabusLink: '/syllabus/aws-devops-roadmap.txt',
  prerequisites: [
    'Basic computer usage and confidence with terminal commands',
    'No prior cloud knowledge required (beginner friendly)',
    'Willingness to practice hands-on labs daily',
    'Laptop with stable internet connection',
  ],
  quickStats: [
    { value: '2 Months', label: 'Structured Plan' },
    { value: '10 Modules', label: 'Complete Syllabus' },
    { value: 'Live Deployments', label: 'Hands-on Outcome' },
  ],
  techStack: [
    {
      name: 'AWS',
      note: 'EC2, S3, IAM, VPC, RDS, scaling',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg',
    },
    {
      name: 'Linux',
      note: 'Permissions, packages, SSH access',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
    },
    {
      name: 'Docker',
      note: 'Container basics and image flow',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
    },
    {
      name: 'GitHub + CI/CD',
      note: 'Version control and deployment pipeline concepts',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
    },
  ],
  studentImages: [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&auto=format&fit=crop&q=80',
  ],
  overviewCards: [
    {
      title: 'Same Course Syllabus, Visual Roadmap',
      text: 'This page translates the existing AWS Cloud with DevOps Essentials syllabus into a week-by-week plan so beginners can understand exactly how learning will progress.',
    },
    {
      title: 'Hands-On First Approach',
      text: 'Every phase includes command-line practice, service setup, and deployment tasks so your confidence is built through execution, not only theory.',
    },
    {
      title: 'Interview-Relevant Outcomes',
      text: 'By the final phase, you can explain AWS core services, basic networking, deployment flow, and monitoring concepts in fresher-level interviews.',
    },
    {
      title: 'Industry Use Cases',
      text: 'The capstone module focuses on realistic deployment decisions, security hygiene, and service integration that mirror practical team workflows.',
    },
  ],
  roadmapSteps: [
    {
      title: 'Weeks 1-2: Cloud, Linux, and Networking Foundation',
      shortLabel: 'Foundation',
      icon: '🌐',
      summary: 'Build the base required to work comfortably with cloud infrastructure.',
      bullets: [
        'Cloud concepts, AWS global infrastructure, pricing basics',
        'Linux basics, permissions, package installation, SSH access',
        'Networking basics: IP, DNS, HTTP/HTTPS, ports, security groups',
      ],
      tooltip: 'Focus on understanding terms deeply; these foundations make every later AWS concept easier.',
    },
    {
      title: 'Weeks 3-4: AWS Core Services + Database Layer',
      shortLabel: 'AWS Core',
      icon: '☁️',
      summary: 'Start using AWS building blocks for actual deployments.',
      bullets: [
        'EC2, S3, IAM, VPC basics, subnets',
        'RDS overview and MySQL on AWS',
        'Backup and connectivity essentials',
      ],
      tooltip: 'By end of this phase, you should be able to launch and connect basic cloud resources independently.',
    },
    {
      title: 'Weeks 5-6: Scaling, Deployment, and DevOps Basics',
      shortLabel: 'Deploy',
      icon: '⚙️',
      summary: 'Move from infrastructure setup to delivery pipelines and reliable deployment.',
      bullets: [
        'Load balancer, traffic distribution, auto scaling',
        'Static website hosting on S3 and web app deployment',
        'Git/GitHub, Docker basics, CI/CD concepts, Nginx, DNS, SSL/HTTPS',
      ],
      tooltip: 'This is where you connect infrastructure and deployment into one flow teams use in production.',
    },
    {
      title: 'Weeks 7-8: Monitoring, Security, and Industry Project',
      shortLabel: 'Project',
      icon: '📈',
      summary: 'Complete the roadmap with practical project delivery and interview preparation.',
      bullets: [
        'CloudWatch basics, logs, alerts, IAM security practices',
        'End-to-end industry live project with deployment',
        'Revision for interviews: AWS service reasoning and deployment discussion',
      ],
      tooltip: 'Final milestone: explain what you built, why you chose each service, and how you ensured reliability.',
    },
  ],
  placementStats: [
    { label: 'Students completing all labs + project', value: 83 },
    { label: 'Interview readiness for cloud support/devops fresher roles', value: 76 },
    { label: 'Practical confidence in deployment workflows', value: 85 },
  ],
  interviewFocus: [
    'IAM, shared responsibility model, and least-privilege access control',
    'AWS core services use-cases: EC2, S3, IAM, VPC, RDS',
    'ALB, Auto Scaling, high availability and fault-tolerant thinking',
    'Linux command flow and permission handling',
    'Networking basics: DNS, ports, security groups/NACL, HTTPS',
    'Deployment lifecycle with Docker, CI/CD, and monitoring',
    'Monitoring and incident basics using logs and CloudWatch',
  ],
  jobRoles: [
    'Cloud Support Associate',
    'Junior AWS Engineer',
    'DevOps Trainee',
    'Site Reliability Trainee (Entry Level)',
    'Infrastructure Support Engineer',
  ],
  faqs: ROADMAP_STUDENT_FAQS,
};

const RoadmapAwsDevOpsPage = () => {
  return <RoadmapCoursePage config={AWS_DEVOPS_ROADMAP_CONFIG} />;
};

export default RoadmapAwsDevOpsPage;
